import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { 
  Image as ImageIcon, 
  Upload, 
  Link as LinkIcon, 
  Package, 
  AlertCircle,
  CheckCircle,
  X,
  Search,
  FileArchive,
  Globe
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminProductImages = () => {
  const [stats, setStats] = useState(null);
  const [pendingProducts, setPendingProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showUrlModal, setShowUrlModal] = useState(false);
  const [showBulkZipModal, setShowBulkZipModal] = useState(false);
  const [showBulkCdnModal, setShowBulkCdnModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Form states
  const [uploadFile, setUploadFile] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [zipFile, setZipFile] = useState(null);
  const [cdnBaseUrl, setCdnBaseUrl] = useState('');
  const [matchField, setMatchField] = useState('manufacturer_sku');
  const [uploading, setUploading] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/product-images/stats`);
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  const fetchPendingProducts = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/product-images/pending?limit=50`);
      const data = await response.json();
      setPendingProducts(data.products || []);
    } catch (error) {
      console.error('Failed to fetch pending products:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchPendingProducts();
  }, [fetchStats, fetchPendingProducts]);

  const handleSingleUpload = async (e) => {
    e.preventDefault();
    if (!selectedProduct || !uploadFile) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      
      const response = await fetch(
        `${API_URL}/api/product-images/upload/${selectedProduct.internal_sku}?set_as_primary=true`,
        { method: 'POST', body: formData }
      );
      
      if (!response.ok) throw new Error('Upload failed');
      
      const data = await response.json();
      toast.success(`Image uploaded for ${selectedProduct.name}`);
      setShowUploadModal(false);
      setUploadFile(null);
      setSelectedProduct(null);
      fetchStats();
      fetchPendingProducts();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSetUrl = async (e) => {
    e.preventDefault();
    if (!selectedProduct || !imageUrl) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image_url', imageUrl);
      formData.append('set_as_primary', 'true');
      
      const response = await fetch(
        `${API_URL}/api/product-images/url/${selectedProduct.internal_sku}`,
        { method: 'PUT', body: formData }
      );
      
      if (!response.ok) throw new Error('Failed to set URL');
      
      toast.success(`Image URL set for ${selectedProduct.name}`);
      setShowUrlModal(false);
      setImageUrl('');
      setSelectedProduct(null);
      fetchStats();
      fetchPendingProducts();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleBulkZipUpload = async (e) => {
    e.preventDefault();
    if (!zipFile) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', zipFile);
      formData.append('match_by', matchField);
      
      const response = await fetch(
        `${API_URL}/api/product-images/bulk/upload-zip`,
        { method: 'POST', body: formData }
      );
      
      if (!response.ok) throw new Error('Bulk upload failed');
      
      const data = await response.json();
      toast.success(`${data.matched} images uploaded successfully!`);
      
      if (data.unmatched_count > 0) {
        toast.warning(`${data.unmatched_count} files could not be matched`);
      }
      
      setShowBulkZipModal(false);
      setZipFile(null);
      fetchStats();
      fetchPendingProducts();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleBulkCdnSet = async (e) => {
    e.preventDefault();
    if (!cdnBaseUrl) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('cdn_base_url', cdnBaseUrl);
      formData.append('match_field', matchField);
      formData.append('extension', '.jpg');
      
      const response = await fetch(
        `${API_URL}/api/product-images/bulk/set-cdn-base`,
        { method: 'PUT', body: formData }
      );
      
      if (!response.ok) throw new Error('Bulk CDN update failed');
      
      const data = await response.json();
      toast.success(`${data.updated} products updated with CDN URLs!`);
      
      setShowBulkCdnModal(false);
      setCdnBaseUrl('');
      fetchStats();
      fetchPendingProducts();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploading(false);
    }
  };

  const filteredProducts = pendingProducts.filter(p => 
    !searchTerm || 
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.internal_sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.manufacturer_sku?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-heading font-semibold text-ivory-800">Product Images</h2>
          <p className="text-ivory-500 font-body mt-1">Upload and manage product images</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => setShowBulkCdnModal(true)}
            className="border-ivory-300"
            data-testid="bulk-cdn-btn"
          >
            <Globe className="w-4 h-4 mr-2" />
            Set CDN URL
          </Button>
          <Button
            onClick={() => setShowBulkZipModal(true)}
            className="bg-gold-500 hover:bg-gold-600 text-white"
            data-testid="bulk-zip-btn"
          >
            <FileArchive className="w-4 h-4 mr-2" />
            Upload ZIP
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <Card className="bg-ivory-100 border-ivory-300">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-ivory-800">{stats.total_products}</p>
              <p className="text-sm text-ivory-500">Total Products</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-700">{stats.with_real_images}</p>
              <p className="text-sm text-green-600">With Images</p>
            </CardContent>
          </Card>
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-amber-700">{stats.using_placeholder}</p>
              <p className="text-sm text-amber-600">Using Placeholder</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-700">{stats.coverage_percent}%</p>
              <p className="text-sm text-blue-600">Coverage</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-ivory-400" />
        <Input
          placeholder="Search products by name, SKU..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="image-search"
        />
      </div>

      {/* Products Needing Images */}
      <Card className="border-ivory-300">
        <CardHeader>
          <CardTitle className="text-lg">Products Needing Images ({pendingProducts.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredProducts.length === 0 ? (
            <div className="text-center py-8 text-ivory-500">
              <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
              <p>All products have images!</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {filteredProducts.map((product) => (
                <div 
                  key={product.internal_sku}
                  className="flex items-center justify-between p-3 bg-ivory-50 rounded-lg hover:bg-ivory-100 transition-colors"
                  data-testid={`pending-product-${product.internal_sku}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-ivory-200 rounded-lg overflow-hidden">
                      <img 
                        src={product.image || 'https://placehold.co/48x48/f5f3f0/9a8b7d?text=?'} 
                        alt="" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="font-medium text-ivory-800">{product.name}</p>
                      <div className="flex gap-2 text-xs text-ivory-500">
                        <span>{product.internal_sku}</span>
                        {product.manufacturer_sku && (
                          <span className="text-ivory-400">• Model: {product.manufacturer_sku}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setSelectedProduct(product); setShowUrlModal(true); }}
                      data-testid={`set-url-${product.internal_sku}`}
                    >
                      <LinkIcon className="w-4 h-4 mr-1" />
                      URL
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => { setSelectedProduct(product); setShowUploadModal(true); }}
                      className="bg-gold-500 hover:bg-gold-600 text-white"
                      data-testid={`upload-${product.internal_sku}`}
                    >
                      <Upload className="w-4 h-4 mr-1" />
                      Upload
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Single Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Image for {selectedProduct?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSingleUpload} className="space-y-4">
            <div>
              <Label>Select Image File</Label>
              <Input
                type="file"
                accept=".jpg,.jpeg,.png,.gif,.webp"
                onChange={(e) => setUploadFile(e.target.files?.[0])}
                data-testid="image-file-input"
              />
              <p className="text-xs text-ivory-500 mt-1">
                Allowed: JPG, PNG, GIF, WebP. Max 10MB
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowUploadModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={uploading || !uploadFile} className="bg-gold-500 hover:bg-gold-600 text-white">
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* URL Modal */}
      <Dialog open={showUrlModal} onOpenChange={setShowUrlModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Image URL for {selectedProduct?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSetUrl} className="space-y-4">
            <div>
              <Label>Image URL</Label>
              <Input
                type="url"
                placeholder="https://cdn.example.com/images/product.jpg"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                data-testid="image-url-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowUrlModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={uploading || !imageUrl} className="bg-gold-500 hover:bg-gold-600 text-white">
                {uploading ? 'Saving...' : 'Set URL'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk ZIP Modal */}
      <Dialog open={showBulkZipModal} onOpenChange={setShowBulkZipModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Bulk Upload from ZIP</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleBulkZipUpload} className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <AlertCircle className="w-4 h-4 inline mr-2" />
              ZIP file should contain images named to match products.
              <br />
              Example: <code className="bg-blue-100 px-1">L99849.jpg</code> matches product with Model &quot;L99849&quot;
            </div>
            <div>
              <Label>ZIP File</Label>
              <Input
                type="file"
                accept=".zip"
                onChange={(e) => setZipFile(e.target.files?.[0])}
                data-testid="zip-file-input"
              />
            </div>
            <div>
              <Label>Match Images By</Label>
              <Select value={matchField} onValueChange={setMatchField}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="model">Manufacturer Model/SKU (e.g., L99849)</SelectItem>
                  <SelectItem value="sku">Internal SKU (e.g., ECA-CRY-0001)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowBulkZipModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={uploading || !zipFile} className="bg-gold-500 hover:bg-gold-600 text-white">
                {uploading ? 'Uploading...' : 'Upload ZIP'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk CDN Modal */}
      <Dialog open={showBulkCdnModal} onOpenChange={setShowBulkCdnModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Set CDN Base URL</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleBulkCdnSet} className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <AlertCircle className="w-4 h-4 inline mr-2" />
              This will set image URLs for all products using the pattern:
              <br />
              <code className="bg-blue-100 px-1">{cdnBaseUrl || 'https://cdn.example.com'}/[model].jpg</code>
            </div>
            <div>
              <Label>CDN Base URL</Label>
              <Input
                type="url"
                placeholder="https://cdn.example.com/images"
                value={cdnBaseUrl}
                onChange={(e) => setCdnBaseUrl(e.target.value)}
                data-testid="cdn-url-input"
              />
            </div>
            <div>
              <Label>Match Field</Label>
              <Select value={matchField} onValueChange={setMatchField}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manufacturer_sku">Manufacturer SKU (e.g., L99849)</SelectItem>
                  <SelectItem value="internal_sku">Internal SKU (e.g., ECA-CRY-0001)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowBulkCdnModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={uploading || !cdnBaseUrl} className="bg-gold-500 hover:bg-gold-600 text-white">
                {uploading ? 'Updating...' : 'Set CDN URLs'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProductImages;
